class ProductStoryNav extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.placeholder = null;
    this.pinned = false;
    this.bound = false;
    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.tabs = Array.from(this.querySelectorAll('[data-story-tab]'));
    this.panels = this.tabs.map((tab) => this.getPanelForTab(tab)).filter(Boolean);
    placeUploadedVideoBelowNav(this);

    this.placeholder = document.createElement('div');
    this.placeholder.className = 'product-story__nav-placeholder';
    this.placeholder.setAttribute('aria-hidden', 'true');
    this.parentNode.insertBefore(this.placeholder, this);

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onClick));
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onScroll);
    requestAnimationFrame(this.onScroll);
  }

  disconnectedCallback() {
    this.tabs.forEach((tab) => tab.removeEventListener('click', this.onClick));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onScroll);
    this.bound = false;
  }

  getHeaderBottom() {
    const bar =
      document.querySelector('sticky-header') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('.header');

    if (!bar) {
      const fallback = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      return Number.isFinite(fallback) ? fallback : 60;
    }

    return Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
  }

  getSection() {
    return this.closest('.product-story');
  }

  getPanelForTab(tab) {
    const id = tab.getAttribute('href')?.slice(1) || '';
    if (id === 'product-reviews') {
      const widget = document.querySelector('#judgeme_product_reviews');
      return widget?.closest('.shopify-section') || widget || this.getVisiblePanel(id);
    }

    return this.getVisiblePanel(id);
  }

  getVisiblePanel(id) {
    const panel = document.getElementById(id);
    if (!panel) return null;

    const style = getComputedStyle(panel);
    if (style.display === 'none' || style.visibility === 'hidden') return null;

    return panel;
  }

  shouldPin(headerBottom) {
    const section = this.getSection();
    if (!section) return false;

    const sectionTop = section.getBoundingClientRect().top;

    // Layout is not ready yet — keep the bar in normal page flow.
    if (window.scrollY < 8 && sectionTop <= 0) {
      return false;
    }

    // Still looking at the product block above Product story.
    if (sectionTop > headerBottom + 1) {
      return false;
    }

    return true;
  }

  pin(headerBottom) {
    this.classList.add('is-fixed');
    this.style.top = `${headerBottom}px`;
    this.placeholder.style.height = '60px';
    this.pinned = true;
  }

  unpin() {
    this.classList.remove('is-fixed');
    this.style.top = '';
    this.placeholder.style.height = '';
    this.pinned = false;
  }

  onResize() {
    this.onScroll();
  }

  onClick(event) {
    const panel = this.getPanelForTab(event.currentTarget);
    if (!panel) return;

    event.preventDefault();
    const offset = this.getHeaderBottom() + this.getBoundingClientRect().height + 8;
    const top = panel.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  onScroll() {
    const headerBottom = this.getHeaderBottom();

    if (this.shouldPin(headerBottom)) {
      this.pin(headerBottom);
    } else {
      this.unpin();
    }

    const marker = headerBottom + this.getBoundingClientRect().height + 24;
    let currentHref = this.tabs[0]?.getAttribute('href');

    this.tabs.forEach((tab) => {
      const panel = this.getPanelForTab(tab);
      if (!panel) return;
      if (panel.getBoundingClientRect().top <= marker) {
        currentHref = tab.getAttribute('href');
      }
    });

    this.tabs.forEach((tab) => {
      tab.classList.toggle('is-active', tab.getAttribute('href') === currentHref);
    });
  }
}

customElements.define('product-story-nav', ProductStoryNav);

class ProductStorySlideshow extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.slides = Array.from(this.querySelectorAll('[data-story-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-story-dot]'));
    this.steps = Array.from(this.querySelectorAll('[data-story-step]'));
    this.prevButton = this.querySelector('[data-story-prev]');
    this.nextButton = this.querySelector('[data-story-next]');
    this.index = 0;
    this.timer = null;
    this.interval = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
    this.autoplayEnabled = this.dataset.autoplay !== 'false' && this.slides.length > 1;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.goTo = this.goTo.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.prevButton?.addEventListener('click', this.prev);
    this.nextButton?.addEventListener('click', this.next);
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });
    this.steps.forEach((step, index) => {
      step.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      });
    });
    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.play);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.play);
    document.addEventListener('visibilitychange', this.onVisibility);

    let touchStartX = 0;
    this.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0].screenX;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        const delta = event.changedTouches[0].screenX - touchStartX;
        if (Math.abs(delta) < 40) return;
        if (delta < 0) this.next();
        else this.prev();
      },
      { passive: true }
    );

    this.goTo(0);
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.bound = false;
  }

  goTo(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === this.index);
    });
    this.steps.forEach((step, stepIndex) => {
      const active = stepIndex === this.index;
      step.classList.toggle('is-active', active);
      if (step.hasAttribute('aria-pressed')) {
        step.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    });
  }

  next() {
    this.goTo(this.index + 1);
    this.restart();
  }

  prev() {
    this.goTo(this.index - 1);
    this.restart();
  }

  play() {
    this.pause();
    if (!this.autoplayEnabled || this.reducedMotion.matches || this.slides.length < 2) return;
    this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
  }

  pause() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    if (this.autoplayEnabled) this.play();
  }

  onVisibility() {
    if (document.hidden) this.pause();
    else this.play();
  }
}

customElements.define('product-story-slideshow', ProductStorySlideshow);

const REVIEW_STAR_PATH =
  'M12 2.4l2.62 5.52 6.08.84-4.42 4.28 1.1 6.06L12 16.3 6.62 19.1l1.1-6.06-4.42-4.28 6.08-.84L12 2.4z';

function formatReviewCount(count) {
  const value = Number(count) || 0;
  if (value >= 1000) {
    const thousands = value / 1000;
    const compact = thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1).replace(/\.0$/, '');
    return `${compact}k`;
  }
  return String(value);
}

function starSvg(fillClass, extra = '') {
  return `<svg class="${fillClass}" viewBox="0 0 24 24" aria-hidden="true"${extra}><path d="${REVIEW_STAR_PATH}" /></svg>`;
}

function renderAverageStars(rating, blockClass = 'product-reviews-breakdown') {
  const score = Math.max(0, Math.min(5, Number(rating) || 0));
  let html = '';

  for (let index = 0; index < 5; index += 1) {
    const fill = Math.max(0, Math.min(1, score - index)) * 100;
    html += `<span class="${blockClass}__star" aria-hidden="true">
      ${starSvg(`${blockClass}__star-empty`)}
      <span class="${blockClass}__star-fill" style="width:${fill}%">${starSvg(`${blockClass}__star-full`)}</span>
    </span>`;
  }

  return html;
}

function fillPriceRating(data) {
  const root = document.querySelector('[data-price-rating]');
  if (!root || root.dataset.ready === 'true' || !data) return;

  const rating = Number.parseFloat(data.average_rating || '0') || 0;
  const count = Number(data.number_of_reviews) || 0;
  if (count <= 0) return;

  const stars = root.querySelector('[data-price-rating-stars]');
  const countEl = root.querySelector('[data-price-rating-count]');
  if (!stars || !countEl) return;

  stars.innerHTML = renderAverageStars(rating, 'product-price-rating');
  countEl.textContent = `${count} Reviews`;
  root.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars, ${count} Reviews`);
  root.hidden = false;
  root.dataset.ready = 'true';
}

function placeUploadedVideoBelowNav(nav) {
  const video = document.querySelector('.video-section');
  const section = video && video.closest('.shopify-section');
  if (!section || section.dataset.storyVideoPlaced === 'true') return;
  nav.insertAdjacentElement('afterend', section);
  section.dataset.storyVideoPlaced = 'true';
}

function renderHistogram(data) {
  const rows = Array.isArray(data.histogram) ? [...data.histogram] : [];
  rows.sort((a, b) => Number(b.rating) - Number(a.rating));

  return rows
    .map((row) => {
      const rating = Number(row.rating) || 0;
      const frequency = Number(row.frequency) || 0;
      const percentage = Math.max(0, Math.min(100, Number(row.percentage) || 0));
      return `<div class="product-reviews-breakdown__row">
        <span class="product-reviews-breakdown__label">${rating}</span>
        ${starSvg('product-reviews-breakdown__row-star')}
        <span class="product-reviews-breakdown__track">
          <span class="product-reviews-breakdown__fill" style="width:${percentage}%"></span>
        </span>
        <span class="product-reviews-breakdown__freq">${formatReviewCount(frequency)}</span>
      </div>`;
    })
    .join('');
}

function buildReviewsBreakdown(data) {
  const root = document.createElement('div');
  root.id = 'product-reviews-breakdown';
  root.className = 'product-reviews-breakdown';

  const rating = Number.parseFloat(data.average_rating || '0') || 0;
  const count = Number(data.number_of_reviews) || 0;
  const summary =
    data.review_summary_text ||
    `Based on ${count} ${count === 1 ? 'review' : 'reviews'}`;

  root.innerHTML = `
    <div class="product-reviews-breakdown__score">
      <p class="product-reviews-breakdown__value">${rating.toFixed(1)}</p>
      <div class="product-reviews-breakdown__stars" aria-hidden="true">${renderAverageStars(rating)}</div>
    </div>
    <p class="product-reviews-breakdown__count">${summary}</p>
    <div class="product-reviews-breakdown__bars">${renderHistogram(data)}</div>
  `;

  return root;
}

function mountReviewsBreakdown() {
  const widget = document.querySelector('#judgeme_product_reviews');
  if (!widget) return;

  const productId = widget.getAttribute('data-product-id') || widget.getAttribute('data-id');
  const data = window.jdgm?.data?.reviewWidget?.[productId];
  if (data) fillPriceRating(data);

  const host = widget.querySelector('.jm-review-widget__header-and-body');
  if (!host || host.querySelector('#product-reviews-breakdown')) return;
  if (!data) return;

  const body = host.querySelector('.jm-review-widget__body');
  const breakdown = buildReviewsBreakdown(data);
  if (body) host.insertBefore(breakdown, body);
  else host.appendChild(breakdown);
}

function initReviewsBreakdown() {
  const widget = document.querySelector('#judgeme_product_reviews');
  if (!widget) return;

  mountReviewsBreakdown();
  const observer = new MutationObserver(() => mountReviewsBreakdown());
  observer.observe(widget, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReviewsBreakdown);
} else {
  initReviewsBreakdown();
}
